/**
 * Navigation model (TMMAPP-0004, design §14.3; v2 sidebar, TMMAPP-0150 U2, visual contract v2 §3,
 * P:22–73; TMMAPP-0159 §B): the five destinations, the Money Tools submenu with its tier rules, and
 * which item a route belongs to — the submenu opens on any of its pages, and a detail page counts as
 * its list (an account page is My Accounts, a virtual account or envelope page is Virtual Accounts, a
 * payee page is Payees, a meeting session is My Money Meetings).
 */
import type { MeVirtualAccount } from '@/api/me';

/** The top-level destinations, in the prototype's order. */
export type NavKey = 'home' | 'assistant' | 'meetings' | 'community' | 'tools';

/** The Money Tools submenu (Operator, 2026-10-17: the prototype's, plus Payees and Cash flow). */
export type ToolKey =
  | 'overview'
  | 'accounts'
  | 'virtualAccounts'
  | 'payees'
  | 'recurring'
  | 'cashFlow'
  | 'audit'
  | 'settings';

export interface NavItem<K extends string = NavKey> {
  key: K;
  path: string;
  /** Subscriber-only surfaces: a GUEST or NONE tier does not see the nav item (§14.3). */
  subscriberOnly: boolean;
  /** Hidden from a GUEST. Audit stays on each shared virtual account's Activity tab. */
  hideFromGuest?: boolean;
}

/**
 * The destinations (catalog `nav.<key>`). Everyone sees all five: a guest reaches Help & feedback and
 * the shared sessions they were invited to (TMMAPP-0142 M11), Home and Community (Operator,
 * 2026-10-17), and My Money Meetings, the meetings they're in (TMMAPP-0159 §B 4, 7); Money Tools
 * holds what their tier allows.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  { key: 'home', path: '/me/home', subscriberOnly: false },
  { key: 'assistant', path: '/me/assistant', subscriberOnly: false },
  { key: 'meetings', path: '/me/meetings', subscriberOnly: false },
  { key: 'community', path: '/community', subscriberOnly: false },
  { key: 'tools', path: '/me/tools', subscriberOnly: false },
];

/** The Money Tools submenu (catalog `toolsNav.<key>`), with today's tier rules. */
export const TOOL_ITEMS: readonly NavItem<ToolKey>[] = [
  { key: 'overview', path: '/me/tools', subscriberOnly: false },
  { key: 'accounts', path: '/me/accounts', subscriberOnly: true },
  { key: 'virtualAccounts', path: '/me/virtual-accounts', subscriberOnly: false },
  { key: 'payees', path: '/me/payees', subscriberOnly: true },
  { key: 'recurring', path: '/me/recurring', subscriberOnly: true },
  { key: 'cashFlow', path: '/me/cash-flow', subscriberOnly: true },
  { key: 'audit', path: '/me/audit', subscriberOnly: false, hideFromGuest: true },
  // TMMAPP-0151 §K: the Money Tools entry opens the prototype's view, the Sharing tab.
  { key: 'settings', path: '/me/settings?tab=sharing', subscriberOnly: false },
];

/** The path itself, or a path under it (`/me/meeting` never matches `/me/meetings`). */
function under(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

/** A meeting's session (`/v/{id}/meetings/…`): My Money Meetings, not Virtual Accounts. */
const MEETING_SESSION_ROUTE = /^\/v\/[^/]+\/meetings(\/|$)/;

/**
 * The Money Tools item a route belongs to, or `null` off the tools. Detail pages count as their
 * list, and the AI activity page (linked from Settings) is Settings. A meeting session belongs to
 * My Money Meetings, off the tools.
 */
export function activeTool(pathname: string): ToolKey | null {
  if (pathname === '/me/tools') return 'overview';
  if (MEETING_SESSION_ROUTE.test(pathname)) return null;
  if (under(pathname, '/v') || under(pathname, '/me/virtual-accounts')) return 'virtualAccounts';
  if (
    under(pathname, '/me/accounts') ||
    under(pathname, '/me/holdings') ||
    under(pathname, '/me/search') ||
    under(pathname, '/connect/plaid/return')
  ) {
    return 'accounts';
  }
  if (under(pathname, '/me/payees')) return 'payees';
  if (under(pathname, '/me/recurring') || under(pathname, '/me/planned')) return 'recurring';
  if (under(pathname, '/me/cash-flow')) return 'cashFlow';
  if (under(pathname, '/me/audit')) return 'audit';
  if (under(pathname, '/me/settings') || under(pathname, '/me/ai-requests')) return 'settings';
  return null;
}

/** The destination a route belongs to: a meeting session is My Money Meetings, a tool page Money Tools. */
export function activeNav(pathname: string): NavKey | null {
  if (MEETING_SESSION_ROUTE.test(pathname)) return 'meetings';
  if (activeTool(pathname) !== null) return 'tools';
  const item = NAV_ITEMS.find((entry) => entry.key !== 'tools' && under(pathname, entry.path));
  return item?.key ?? null;
}

/** Whether a tier sees an item (`useSubscriberAction` covers the actions inside the pages). */
export function visibleTo<K extends string>(
  item: NavItem<K>,
  { subscriber, guest }: { subscriber: boolean; guest: boolean },
): boolean {
  if (item.subscriberOnly && !subscriber) return false;
  if (item.hideFromGuest === true && guest) return false;
  return true;
}

/**
 * One flat list: the caller's own virtual accounts first, then the ones shared with them, each
 * part in `/me` order.
 */
export function orderVirtualAccounts(virtualAccounts: MeVirtualAccount[]): MeVirtualAccount[] {
  return [...virtualAccounts.filter((d) => d.mine), ...virtualAccounts.filter((d) => !d.mine)];
}
